// Sesion activa: quien esta usando la Consola y en que empresa.
import { conCache } from '../lib/cacheLocal';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { currentUser, signOut } from '../api/auth';
import { licencia } from '../api/crm';
import { filasPermisos } from '../api/permisos';
import { getSession, onSessionChange, type Session } from '../api/http';
import type { Licencia } from '../api/types';
import { permisosDePersona } from '../lib/permisos';

export type Yo = { miembroId?: string; nombre: string; email?: string };

type Ctx = {
  session: Session;
  yo: Yo;
  licencia: Licencia | null;
  // Administrador de la empresa (rol con permiso WORKSPACE): ve la seccion Administracion.
  esAdmin: boolean;
  // Ve el trabajo de todos los grupos (campanas): administradores y los roles Supervisor y Gerencia. Lo demas va
  // por permisos (puede).
  esSupervisor: boolean;
  // Roles y permisos (D99): lo que puede hacer segun sus roles (lib/permisos.ts). El administrador puede todo.
  puede: (clave: string) => boolean;
  // Mientras llega "quien soy" todavia no se sabe si es administrador.
  cargandoYo: boolean;
  tieneModulo: (m: string) => boolean;
  salir: () => void;
};

const SessionContext = createContext<Ctx | null>(null);

export function useSesionActiva() {
  const [s, setS] = useState<Session | null>(getSession());
  useEffect(() => onSessionChange(setS), []);
  return s;
}

export function SessionProvider({ session, children }: { session: Session; children: ReactNode }) {
  const me = useQuery({
    queryKey: ['yo', session.workspace.id, session.modo],
    ...conCache('yo', currentUser),
    enabled: session.modo === 'usuario',
    staleTime: 10 * 60_000,
    // Sin "quien soy" desaparecen menus (Admin, Reportes) y buzones de grupo: se insiste antes de rendirse.
    retry: 4,
    retryDelay: (n) => Math.min(1000 * 2 ** n, 10_000),
  });
  const filas = useQuery({ queryKey: ['permisos-rol', session.workspace.id], ...conCache('permisos-rol', filasPermisos), enabled: session.modo === 'usuario', staleTime: 5 * 60_000, retry: 2 });
  const lic = useQuery({ queryKey: ['licencia'], ...conCache('licencia', licencia), staleTime: 5 * 60_000, retry: 1 });

  const u = me.data;
  const yo: Yo =
    session.modo === 'usuario'
      ? {
          miembroId: u?.workspaceMember?.id,
          nombre: `${u?.workspaceMember?.name.firstName ?? u?.firstName ?? ''} ${u?.workspaceMember?.name.lastName ?? u?.lastName ?? ''}`.trim() || session.email || 'Tú',
          email: u?.email ?? session.email,
        }
      : { nombre: 'Puesto compartido' };

  const L = lic.data ?? null;
  const esAdmin = session.modo === 'usuario' && !!u?.currentUserWorkspace?.permissionFlags?.includes('WORKSPACE');
  // Sin la lista guardada (aun cargando o sin el objeto) cada rol usa sus permisos de fabrica.
  const permisos = session.modo === 'usuario' ? permisosDePersona({ admin: esAdmin, roles: u?.workspaceMember?.roles ?? [], filas: filas.data ?? [] }) : new Set<string>();
  const value: Ctx = {
    session: { ...session, workspace: { ...session.workspace, nombre: u?.currentWorkspace?.displayName || session.workspace.nombre } },
    yo,
    licencia: L,
    esAdmin,
    esSupervisor:
      session.modo === 'usuario' && (!!u?.currentUserWorkspace?.permissionFlags?.includes('WORKSPACE') || !!u?.workspaceMember?.roles?.some((r) => r.label === ROL_SUPERVISOR || r.label === 'Gerencia')),
    puede: (clave) => permisos.has(clave),
    cargandoYo: session.modo === 'usuario' && (me.isPending || (me.isFetching && !me.data) || (filas.isPending && filas.fetchStatus !== 'idle')),
    // Sin la app Xmartek 3CX (sin licencia legible) el CRM basico sigue funcionando.
    tieneModulo: (m) => !L || (L.permiteUso && L.modulos.includes(m)),
    salir: signOut,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const c = useContext(SessionContext);
  if (!c) throw new Error('useSession fuera de SessionProvider');
  return c;
}

// Nombre del rol que ve reportes sin administrar (lo crea Administracion -> Usuarios y el alta del Portal).
export const ROL_SUPERVISOR = 'Supervisor';

export const PLAN_NOMBRE: Record<string, string> = {
  ESENCIAL: 'Esencial',
  PROFESIONAL: 'Profesional',
  CONTACT_CENTER: 'Contact Center',
};
